import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import {
  GaugeChartContainer,
  GaugeChartWrapper,
  GaugeChartLabel,
} from './GaugeChart.styled';

interface GaugeChartProps {
  value: number;
  min?: number;
  max?: number;
  color: string;
  backgroundColor?: string;
  isShowPercentage?: boolean;
  decimals?: number;
  label?: string;
  size?: number;
  innerRadius?: number;
  outerRadius?: number;
  startAngle?: number;
  endAngle?: number;
  isEqScore?: boolean;
}

export function GaugeChart ({
  value,
  min = 0,
  max = 100,
  color,
  backgroundColor = '#d9d9d9',
  isShowPercentage = false,
  decimals = 0,
  label,
  size = 180,
  innerRadius = 68,
  outerRadius = 78,
  startAngle = 180,
  endAngle = 0,
  isEqScore = false,
}: GaugeChartProps) {
  
  const getColorForValue = (val: number, isEquality: boolean): string => {
    if (!isEquality) {
      return color;
    }

    const colors = ['#FF5733', '#FF8C42', '#FFA500', '#90EE90', '#32CD32'];

    const normalizedVal = (val - 1) / 4;
    const index = Math.floor(normalizedVal * (colors.length - 1));
    const nextIndex = Math.min(index + 1, colors.length - 1);
    const t = (normalizedVal * (colors.length - 1)) % 1;

    const color1 = colors[index];
    const color2 = colors[nextIndex];

    const r1 = parseInt(color1.slice(1, 3), 16);
    const g1 = parseInt(color1.slice(3, 5), 16);
    const b1 = parseInt(color1.slice(5, 7), 16);
    const r2 = parseInt(color2.slice(1, 3), 16);
    const g2 = parseInt(color2.slice(3, 5), 16);
    const b2 = parseInt(color2.slice(5, 7), 16);

    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);

    return `#${r.toString(16).padStart(2, '0')}${g
      .toString(16)
      .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  const safeMin = isEqScore ? 1 : Math.max(0, min);
  const safeMax = isEqScore ? 5 : Math.max(safeMin + 1, max);

  const normalizedValue = Math.max(
    0,
    Math.max(safeMin, Math.min(safeMax, value))
  );

  const gradientColor = isEqScore
    ? getColorForValue(normalizedValue, true)
    : color;

  const range = safeMax - safeMin;
  const percentage =
    range > 0 ? ((normalizedValue - safeMin) / range) * 100 : 0;

  const filledValue = Math.max(0, Math.min(100, percentage));
  const emptyValue = Math.max(0, 100 - filledValue);

  const data = [{ value: filledValue }, { value: emptyValue }];

  const displayValue =
    decimals === 0
      ? Math.floor(normalizedValue)
      : normalizedValue.toFixed(decimals);

  return (
    <GaugeChartContainer>
      <GaugeChartWrapper size={size}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              startAngle={startAngle}
              endAngle={endAngle}
              innerRadius={`${innerRadius}%`}
              outerRadius={`${outerRadius}%`}
              cornerRadius={10}
              paddingAngle={-3}
              data={data}
              dataKey="value"
              blendStroke={false}
              isAnimationActive={false}
            >
              <Cell fill={gradientColor} />
              <Cell fill={backgroundColor} />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <GaugeChartLabel>
          {displayValue}
          {isShowPercentage && '%'}
        </GaugeChartLabel>
        {label && <GaugeChartLabel secondary={false}>{label}</GaugeChartLabel>}
      </GaugeChartWrapper>
    </GaugeChartContainer>
  );
}
